/* eslint-disable max-lines */

import type { PayloadAction } from '@reduxjs/toolkit';
import config from 'Cloud/config';
import escapeForHtml from 'lib/escapeForHtml';
import { CreateAlbumAPICall } from 'reactApp/api/albums/CreateAlbumAPICall';
import { CreateFamilyAlbumApiCall } from 'reactApp/api/family/CreateFamilyAlbumApiCall';
import { CreateFamilyFolderApiCall } from 'reactApp/api/family/CreateFamilyFolderApiCall';
import { MakeFamilyFolderCommonApiCall } from 'reactApp/api/family/MakeFamilyFolderCommonApiCall';
import { IS_BIZ_USER } from 'reactApp/appHelpers/configHelpers';
import { publishHelper } from 'reactApp/appHelpers/publishHelper';
import { EButtonType } from 'reactApp/components/BaseConfirmDialog/BaseConformationDialog.types';
import { renderFamilyFolderModal } from 'reactApp/components/FamilyFolderModal/FamilyFolderModal.helpers';
import { renderNewFolderModal } from 'reactApp/components/NewFolderModal/NewFolderModal.helpers';
import { closeSharingNew } from 'reactApp/components/SharingNewBiz/SharingNew.helpers';
import type { EPromoTooltip } from 'reactApp/components/SharingWindow/Sharing.types';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getFamilyAlbumsFeature, getFamilyFoldersFeature } from 'reactApp/modules/family/family.selectors';
import { familyContentUpdateFolderList } from 'reactApp/modules/familyContent/familyContent.module';
import { getFeatureABCreateFolderNewFlow } from 'reactApp/modules/features/features.selectors';
import { changeHomeHistory } from 'reactApp/modules/home/home.actions';
import { waitForFolderBeingLoaded } from 'reactApp/modules/home/home.saga';
import { getHomeItemById } from 'reactApp/modules/home/home.selectors';
import {
    createFamilyFolderSuccess,
    createFolderSuccess,
    makeFamilyFolderCommonSuccess,
    makeFamilyFolderSuccess,
    silentlyDeleteFamilyFolderOnFailedCreation,
} from 'reactApp/modules/modifying/modifying.actions';
import { addFolderHelper } from 'reactApp/modules/modifying/modifying.helpers';
import {
    type CreateFamilyAlbumActionInterface,
    type CreateFamilyFolderActionInterface,
    type ICreateFolderAction,
    type ICreateFolderSuccess,
    EModifyReason,
    FolderType,
} from 'reactApp/modules/modifying/modifying.types';
import { closePopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { CSAT_TRIGGER_EVENTS, pushCSATTriggerEvent } from 'reactApp/modules/promo/sagas/initCSATSurvey';
import { changeHistory } from 'reactApp/modules/router/router.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { selectOne, startSelecting } from 'reactApp/modules/selections/selections.actions';
import { getCurrentSharedTab } from 'reactApp/modules/shared/shared.selectors';
import { SharedTab } from 'reactApp/modules/shared/shared.type';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { snackbarController } from 'reactApp/modules/snackbar/snackbar.controller';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { isDomainFolder } from 'reactApp/modules/storage/folder.helpers';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { scrollToItemAction } from 'reactApp/modules/storage/storage.module';
import { getCurrentFolder, hasParentsDomainFolder } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { setShowUploaderAction } from 'reactApp/modules/uploadList/uploadList.module';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { store as reduxStore, store } from 'reactApp/store';
import { openDisabledFeaturePopupHelper } from 'reactApp/ui/DisabledFeatureDialog/DisabledFeatureDialog.helpers';
import { DisabledFeature } from 'reactApp/ui/DisabledFeatureDialog/DisabledFeatureDialog.types';
import { getDefaultAlbumName } from 'reactApp/utils/formatDate';
import { sendDwh } from 'reactApp/utils/ga';
import { getGaFolderType } from 'reactApp/utils/getGaFolderType';
import { noop, noopVoid } from 'reactApp/utils/helpers';
import opener from 'reactApp/utils/opener';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { truncateTextMiddle } from 'reactApp/utils/textHelpers';
import { channel } from 'redux-saga';
import { call, put, select, take } from 'redux-saga/effects';

import { createFolderErrors } from '../modifying.constants';

const MAX_NAME_LENGTH = config.get('ITEM_NAME_MAX_LENGTH');

const createFamilyAlbumApiCall = (params) => new CreateFamilyAlbumApiCall().makeRequest(params);
const createFamilyFolderApiCall = (params) => new CreateFamilyFolderApiCall().makeRequest(params);
const makeFamilyFolderCommonApiCall = (params) => new MakeFamilyFolderCommonApiCall().makeRequest(params);
const createAlbumAPICall = ({ title }) => new CreateAlbumAPICall().makeRequest({ title });

export function createFolderRequest({
    folderName,
    parent,
    skipXhr = undefined,
    conflictMode = undefined,
    publishPromoTooltip = undefined as EPromoTooltip | undefined,
    storage,
    share,
    gaPrefix,
    createdInToolbar = false,
    onSnackbarClick = noopVoid,
}): Promise<{ folderName: string }> {
    return new Promise((resolve, reject) => {
        folderName = folderName.trim();

        if (!folderName) {
            return reject(createFolderErrors.getMessage('required'));
        }

        if (folderName.length > MAX_NAME_LENGTH) {
            return reject(createFolderErrors.getMessage('name_too_long'));
        }

        addFolderHelper({
            item: folderName,
            parent,
            storage,
            skipXhr,
            conflictMode,
            reason: EModifyReason.add,
        })
            .then((item: any) => {
                store.dispatch(
                    createFolderSuccess({
                        storage,
                        share,
                        folderName: item.id,
                        gaPrefix,
                        publishPromoTooltip,
                        createdInToolbar,
                        onSnackbarClick,
                    })
                );
                resolve({ folderName: item.id, ...item });
            })
            .catch((error) => {
                reject(
                    createFolderErrors.getMessage((error?.home && error?.home.error) || error, {
                        '{FOLDER_NAME}': escapeForHtml(folderName),
                    })
                );
            });
    });
}

export function createFamilyFolderRequest({
    folderName,
    parent,
    skipXhr = undefined,
    conflictMode = undefined,
    publishPromoTooltip = undefined as EPromoTooltip | undefined,
    storage,
    share,
    gaPrefix,
    createdInToolbar = false,
}): Promise<{ folderName: string }> {
    return new Promise((resolve, reject) => {
        folderName = folderName.trim();

        if (!folderName) {
            return reject(createFolderErrors.getMessage('required'));
        }

        if (folderName.length > MAX_NAME_LENGTH) {
            return reject(createFolderErrors.getMessage('name_too_long'));
        }

        addFolderHelper({
            item: folderName,
            parent,
            storage,
            skipXhr,
            conflictMode,
            reason: EModifyReason.add,
            folderType: FolderType.family,
        })
            .then((item: any) => {
                createFamilyFolderApiCall({ home: item.id })
                    .then(() => {
                        store.dispatch(
                            createFamilyFolderSuccess({
                                folderName: item.id,
                                storage,
                                share,
                                gaPrefix,
                                publishPromoTooltip,
                                createdInToolbar,
                            })
                        );
                        resolve({ folderName: item.id, ...item });
                    })
                    .catch((error) => {
                        store.dispatch(silentlyDeleteFamilyFolderOnFailedCreation({ item }));

                        reject(
                            createFolderErrors.getMessage((error?.home && error?.home.error) || error, {
                                '{FOLDER_NAME}': escapeForHtml(folderName),
                            })
                        );
                    });
            })
            .catch((error) => {
                reject(
                    createFolderErrors.getMessage((error?.home && error?.home.error) || error, {
                        '{FOLDER_NAME}': escapeForHtml(folderName),
                    })
                );
            });
    });
}

export function createFamilyAlbumRequest({ folderName }): Promise<{ id: string }> {
    return new Promise((resolve, reject) => {
        folderName = folderName.trim();

        if (!folderName) {
            return reject(createFolderErrors.getMessage('required'));
        }

        if (folderName.length > MAX_NAME_LENGTH) {
            return reject(createFolderErrors.getMessage('name_too_long'));
        }

        createAlbumAPICall({ title: folderName })
            .then(({ data }) => {
                createFamilyAlbumApiCall({ album_id: data.id })
                    .then(() => {
                        store.dispatch(
                            createFamilyFolderSuccess({
                                folderName: data.id,
                            })
                        );
                        resolve({ id: data.id });
                    })
                    .catch((error) => {
                        reject(
                            createFolderErrors.getMessage((error?.home && error?.home.error) || error, {
                                '{FOLDER_NAME}': escapeForHtml(folderName),
                            })
                        );
                    });
            })
            .catch((error) => {
                reject(
                    createFolderErrors.getMessage((error?.home && error?.home.error) || error, {
                        '{FOLDER_NAME}': escapeForHtml(folderName),
                    })
                );
            });
    });
}

export function* createNewFolder(action: ICreateFolderAction) {
    const {
        parentFolderId,
        showCreateButton,
        showShareButton = true,
        storage,
        gaPrefix,
        onCreateFolder,
        onClose = noopVoid,
        createdInToolbar,
    } = action;

    const isCorpUser = yield select(UserSelectors.isCorpUser);
    const isBizUser = yield select(UserSelectors.isBizUser);
    const isOverquotaBySpaceAndApi = yield select(UserQuotaSelectors.isOverquotaBySpaceAndApi);
    const isOverquotaBySpace = yield select(UserQuotaSelectors.isOverquota);
    const isOverquota = isBizUser ? isOverquotaBySpace : isOverquotaBySpaceAndApi;

    const currentFolder = yield select(getCurrentFolder);
    const hasParentsDmnFolder = yield select(hasParentsDomainFolder, currentFolder);
    const ignoreUserOverquota = isDomainFolder(currentFolder) || hasParentsDmnFolder || isCorpUser;

    if (isOverquota && !ignoreUserOverquota) {
        openDisabledFeaturePopupHelper({ disabledFeature: DisabledFeature.newFolder });
        return;
    }

    yield renderNewFolderModal(
        {
            createFolder: async (folderName: string, share: boolean) => {
                const { folderName: newFolder } = await createFolderRequest({
                    folderName,
                    share,
                    parent: parentFolderId,
                    storage,
                    gaPrefix,
                    createdInToolbar,
                });

                if (newFolder) {
                    onCreateFolder?.(newFolder);
                }
            },
            showShareButton,
            showCreateButton,
        },
        onClose
    );
}

export function* createFamilyFolder(action: CreateFamilyFolderActionInterface) {
    const {
        parentFolderId,
        showCreateButton,
        showShareButton = true,
        storage,
        gaPrefix,
        onCreateFolder,
        onClose = noopVoid,
        createdInToolbar,
    } = action;

    const isCorpUser = yield select(UserSelectors.isCorpUser);
    const isBizUser = yield select(UserSelectors.isBizUser);
    const isOverquotaBySpaceAndApi = yield select(UserQuotaSelectors.isOverquotaBySpaceAndApi);
    const isOverquotaBySpace = yield select(UserQuotaSelectors.isOverquota);
    const isOverquota = isBizUser ? isOverquotaBySpace : isOverquotaBySpaceAndApi;

    const currentFolder = yield select(getCurrentFolder);
    const hasParentsDmnFolder = yield select(hasParentsDomainFolder, currentFolder);
    const ignoreUserOverquota = isDomainFolder(currentFolder) || hasParentsDmnFolder || isCorpUser;

    if (isOverquota && !ignoreUserOverquota) {
        openDisabledFeaturePopupHelper({ disabledFeature: DisabledFeature.newFolder });
        return;
    }

    yield renderFamilyFolderModal(
        {
            createFolder: async (folderName: string, share: boolean) => {
                const { folderName: newFolder } = await createFamilyFolderRequest({
                    folderName,
                    share,
                    parent: parentFolderId,
                    storage,
                    gaPrefix,
                    createdInToolbar,
                });

                if (newFolder) {
                    onCreateFolder?.(newFolder);
                    store.dispatch(changeHomeHistory({ id: newFolder }));
                }
            },
            showShareButton,
            showCreateButton,
        },
        onClose
    );
}

export function* createFamilyAlbum(action: CreateFamilyAlbumActionInterface) {
    const { showCreateButton, showShareButton = true, onClose = noopVoid } = action;

    const isCorpUser = yield select(UserSelectors.isCorpUser);
    const isBizUser = yield select(UserSelectors.isBizUser);
    const isOverquotaBySpaceAndApi = yield select(UserQuotaSelectors.isOverquotaBySpaceAndApi);
    const isOverquotaBySpace = yield select(UserQuotaSelectors.isOverquota);
    const isOverquota = isBizUser ? isOverquotaBySpace : isOverquotaBySpaceAndApi;

    const currentFolder = yield select(getCurrentFolder);
    const hasParentsDmnFolder = yield select(hasParentsDomainFolder, currentFolder);
    const ignoreUserOverquota = isDomainFolder(currentFolder) || hasParentsDmnFolder || isCorpUser;
    const folderName = getDefaultAlbumName();

    if (isOverquota && !ignoreUserOverquota) {
        openDisabledFeaturePopupHelper({ disabledFeature: DisabledFeature.newFolder });
        return;
    }

    const albumChannel = channel();

    renderFamilyFolderModal(
        {
            createFolder: async (folderName: string) => {
                const { id } = await createFamilyAlbumRequest({
                    folderName,
                });

                albumChannel.put({ id });
            },
            showShareButton,
            showCreateButton,
            folderName,
            title: 'Создание семейного альбома',
            description: 'Доступен всем участникам подписки',
            buttons: [EButtonType.accept, EButtonType.cancel],
        },
        onClose
    );

    const { id } = yield take(albumChannel);
    albumChannel.close();

    if (id) {
        yield put(changeHistory(`/albums/custom/${id}`));
    }
}

export function* handleCreateNewFolder(action: PayloadAction<ICreateFolderAction>) {
    yield createNewFolder(action.payload);
}

export function* handleCreateFamilyFolder(action: PayloadAction<CreateFamilyFolderActionInterface>) {
    yield createFamilyFolder(action.payload);
}

export function* handleCreateFamilyAlbum(action: PayloadAction<CreateFamilyAlbumActionInterface>) {
    yield createFamilyAlbum(action.payload);
}

export function* handleCreateFolderSuccess(action: PayloadAction<ICreateFolderSuccess>) {
    const { folderName, share, storage = '', publishPromoTooltip, createdInToolbar, onSnackbarClick } = action.payload;
    const currentStorage = yield select(getCurrentStorage);
    const sharedTab = yield select(getCurrentSharedTab);
    const { isHome } = getStorage(storage || currentStorage);
    // tempexp-14737-start
    const isPhone = yield select(EnvironmentSelectors.isPhone);
    const isNewFlowEnabled = yield select(getFeatureABCreateFolderNewFlow);
    // tempexp-14737-end

    const radarAction = 'open-folder';

    yield closePopupHelper(popupNames.CREATE_FOLDER);

    const folder = yield select(getHomeItemById, folderName);

    if (!folder) {
        return;
    }

    const destination = folder.id;
    if (IS_BIZ_USER || !isNewFlowEnabled) {
        yield put(changeHomeHistory({ id: destination }));
        yield call(waitForFolderBeingLoaded, destination);
        sendPaymentGa({
            eventCategory: ECategoryGa.folder,
            action: radarAction,
            type_folder: getGaFolderType(folder),
            source: 'auto',
        });
    } else {
        if (!createdInToolbar) {
            // С бэка инфа о автоудаляемых приходит в нижнем регистре, поэтому эмулируем аналогичное поведение
            const folderId = currentStorage === EStorageType.sharedAutodelete ? folder?.id?.toLowerCase() : folder?.id;

            store.dispatch(selectOne({ selectedIdx: folderId, storage: storage as EStorageType }));
            if (isPhone) {
                store.dispatch(setShowUploaderAction(false));
                store.dispatch(startSelecting());
            }

            store.dispatch(scrollToItemAction(folderId));
        }

        if ((!share || (share && currentStorage === EStorageType.sharedAutodelete)) && folder && folder.id !== '/') {
            const snackbarItemNameMaxLength = 20;

            snackbarController.showSnackbar({
                id: 'create-folder-success-with-link',
                text: `Папка «${truncateTextMiddle(folder.name, snackbarItemNameMaxLength)}» создана`,
                closable: true,
                type: SnackbarTypes.success,
                buttonText: 'Перейти в папку',
                onButtonClick: () => {
                    store.dispatch(changeHomeHistory({ id: destination }));

                    if (currentStorage === EStorageType.sharedAutodelete) {
                        closeSharingNew();
                    }

                    onSnackbarClick?.();

                    sendPaymentGa({
                        eventCategory: ECategoryGa.folder,
                        action: radarAction,
                        type_folder: getGaFolderType(folder),
                        source: 'snackbar',
                    });
                },
            });
        }
    }

    if (share && folder && folder.id !== '/') {
        publishHelper({
            item: folder,
            itemStorage: EStorageType.home,
            autoSeo: currentStorage === EStorageType.sharedLinks && sharedTab === SharedTab.indexed,
            // tempexp-14737-next-line
            onClose: () => (isHome ? noop : opener(`/home/${encodeURIComponent(destination)}`, true)),
            withPromoTooltip: publishPromoTooltip,
            folderName: !IS_BIZ_USER && folder.name,
        });
    }

    yield put(pushCSATTriggerEvent({ type: CSAT_TRIGGER_EVENTS.createFolder }));
}

export function* handleCreateFamilyFolderSuccess(action: PayloadAction<ICreateFolderSuccess>) {
    const { folderName, share, storage = '', publishPromoTooltip, createdInToolbar } = action.payload;
    const currentStorage = yield select(getCurrentStorage);
    const sharedTab = yield select(getCurrentSharedTab);
    const { isHome } = getStorage(storage || currentStorage);
    // tempexp-14737-start
    const isPhone = yield select(EnvironmentSelectors.isPhone);
    const isNewFlowEnabled = yield select(getFeatureABCreateFolderNewFlow);
    // tempexp-14737-end

    const radarAction = 'open-folder';

    yield closePopupHelper(popupNames.CREATE_FAMILY_FOLDER);

    const folder = yield select(getHomeItemById, folderName);

    if (!folder) {
        return;
    }

    const destination = folder.id;
    if (!isNewFlowEnabled) {
        yield put(changeHomeHistory({ id: destination }));
        yield call(waitForFolderBeingLoaded, destination);
        sendPaymentGa({
            eventCategory: ECategoryGa.folder,
            action: radarAction,
            type_folder: 'family-folder',
            source: 'auto',
        });
    } else {
        if (!createdInToolbar) {
            // С бэка инфа о автоудаляемых приходит в нижнем регистре, поэтому эмулируем аналогичное поведение
            const folderId = currentStorage === EStorageType.sharedAutodelete ? folder?.id?.toLowerCase() : folder?.id;

            store.dispatch(selectOne({ selectedIdx: folderId, storage: storage as EStorageType }));
            if (isPhone) {
                store.dispatch(setShowUploaderAction(false));
                store.dispatch(startSelecting());
            }

            store.dispatch(scrollToItemAction(folderId));
        }

        if ((!share || (share && currentStorage === EStorageType.sharedAutodelete)) && folder && folder.id !== '/') {
            const snackbarItemNameMaxLength = 18;

            snackbarController.showSnackbar({
                id: 'create-folder-success-with-link',
                text: `Папка «${truncateTextMiddle(folder.name, snackbarItemNameMaxLength)}» создана`,
                closable: true,
                type: SnackbarTypes.success,
                buttonText: 'Перейти в папку',
                onButtonClick: () => {
                    store.dispatch(changeHomeHistory({ id: destination }));

                    if (currentStorage === EStorageType.sharedAutodelete) {
                        closeSharingNew();
                    }

                    sendPaymentGa({
                        eventCategory: ECategoryGa.folder,
                        action: radarAction,
                        type_folder: 'family-folder',
                        source: 'snackbar',
                    });
                },
            });
        }
    }

    if (share && folder && folder.id !== '/') {
        publishHelper({
            item: folder,
            itemStorage: EStorageType.home,
            autoSeo: currentStorage === EStorageType.sharedLinks && sharedTab === SharedTab.indexed,
            // tempexp-14737-next-line
            onClose: () => (isHome ? noop : opener(`/home/${encodeURIComponent(destination)}`, true)),
            withPromoTooltip: publishPromoTooltip,
            folderName: !IS_BIZ_USER && folder.name,
        });
    }
}

/**
 * Делаем из папки семейную
 */
export const handleMakeFamilyFolder = (item: any, isOnFolder: boolean) => {
    const state = reduxStore.getState();
    const hasFamilyFoldersFeature = getFamilyFoldersFeature(state);
    const hasFamilyAlbumsFeature = getFamilyAlbumsFeature(state);
    const isFamilyTariff = hasFamilyFoldersFeature && hasFamilyAlbumsFeature;

    createFamilyFolderApiCall({ home: item.id })
        .then(() => {
            sendDwh({
                eventCategory: 'dropdown-make-family-folder-success',
                dwhData: {
                    is_family_member: isFamilyTariff ? 1 : 0,
                },
            });
            store.dispatch(makeFamilyFolderSuccess({ id: item.id }));
            store.dispatch(
                showSnackbarAction({
                    id: 'make-family-folder-success',
                    type: SnackbarTypes.success,
                    text: isOnFolder
                        ? 'Папка стала семейной'
                        : 'Теперь все в семейной подписке могут просматривать и редактировать эту папку',
                    closable: true,
                })
            );
        })
        .catch(() => {
            sendDwh({
                eventCategory: 'dropdown-make-family-folder-failure',
                dwhData: {
                    is_family_member: isFamilyTariff ? 1 : 0,
                },
            });
            store.dispatch(
                showSnackbarAction({
                    id: 'make-family-folder-error',
                    type: SnackbarTypes.failure,
                    text: 'Не удалось открыть папку для семьи. Повторите попытку чуть позже',
                    closable: true,
                })
            );
        });
};

export const handleMakeFamilyFolderCommon = (item: any, isFamilyPage: boolean) => {
    const state = reduxStore.getState();
    const hasFamilyFoldersFeature = getFamilyFoldersFeature(state);
    const hasFamilyAlbumsFeature = getFamilyAlbumsFeature(state);
    const isFamilyTariff = hasFamilyFoldersFeature && hasFamilyAlbumsFeature;

    makeFamilyFolderCommonApiCall({ home: item.id })
        .then(() => {
            sendDwh({
                eventCategory: 'dropdown-undo-family-folder-success',
                dwhData: {
                    is_family_member: isFamilyTariff ? 1 : 0,
                },
            });

            store.dispatch(makeFamilyFolderCommonSuccess({ id: item.id }));
            if (isFamilyPage) {
                store.dispatch(familyContentUpdateFolderList({ id: item.id }));
            }
            store.dispatch(
                showSnackbarAction({
                    id: 'undo-family-folder-success',
                    type: SnackbarTypes.success,
                    text: isFamilyPage
                        ? 'Участники семейной подписки больше\nне видят эту папку'
                        : 'Папка теперь не семейная и видна только\nв общем разделе',
                    closable: true,
                })
            );
        })
        .catch((error) => {
            let errorText = 'Не удалось закрыть папку для семьи. Повторите попытку чуть позже';
            if (error.error === 'user is not owner of family folder') {
                errorText = 'Не удалось закрыть папку для семьи, потому что её создал другой участник';
            }

            sendDwh({
                eventCategory: 'dropdown-undo-family-folder-failure',
                dwhData: {
                    is_family_member: isFamilyTariff ? 1 : 0,
                },
            });
            store.dispatch(
                showSnackbarAction({
                    id: 'undo-family-folder-common-error',
                    type: SnackbarTypes.failure,
                    text: errorText,
                    closable: true,
                })
            );
        });
};
